import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  parseDocxBuffer,
  parsePptxDeck,
  parseXlsxBuffer,
  DocxContent,
  PptxDeck,
  PptxParagraph,
  PptxShape,
  PptxTextRun,
  XlsxSheet,
} from '../../hooks/FileHandler/converters';

interface NativeDocViewerProps {
  url: string;
  title: string;
}

const NATIVE_EXTENSIONS = ['.docx', '.pptx', '.xlsx'] as const;
type NativeExt = typeof NATIVE_EXTENSIONS[number];

const getExtension = (title: string, url: string): string => {
  const candidate = (title || url).toLowerCase();
  const match = candidate.match(/\.[a-z0-9]+(?=\?|#|$)/i);
  return match ? match[0].toLowerCase() : '';
};

const isNativeExt = (ext: string): ext is NativeExt =>
  (NATIVE_EXTENSIONS as readonly string[]).includes(ext);

/**
 * In-app renderer for OpenXML documents. We fetch the file once as an
 * ArrayBuffer and then parse it with our own parsers into structured data,
 * rendering pure HTML/CSS -- no third-party iframes, no CORS, consistent
 * styling with the rest of the app.
 */
const NativeDocViewer: React.FC<NativeDocViewerProps> = ({ url, title }) => {
  const ext = useMemo(() => getExtension(title, url), [title, url]);

  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBuffer(null);
    setError(null);
    setLoading(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((ab) => {
        if (!cancelled) {
          setBuffer(ab);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to download file');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!isNativeExt(ext)) {
    return <UnsupportedFallback url={url} />;
  }

  if (loading) {
    return <div className="native-doc-loading">Loading document…</div>;
  }

  if (error || !buffer) {
    return (
      <div className="native-doc-fallback">
        <p>Couldn't load this file.</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="pdf-link">
          Open in a new tab
        </a>
      </div>
    );
  }

  if (ext === '.docx') return <DocxView buffer={buffer} url={url} />;
  if (ext === '.pptx') return <PptxView buffer={buffer} url={url} title={title} />;
  return <XlsxView buffer={buffer} url={url} />;
};

// ---------------- Shared fallback ----------------

const UnsupportedFallback: React.FC<{ url: string }> = ({ url }) => (
  <div className="native-doc-fallback">
    <p>In-app preview isn't available for this file type.</p>
    <a href={url} target="_blank" rel="noopener noreferrer" className="pdf-link">
      Download / open in a new tab
    </a>
  </div>
);

// ---------------- DOCX ----------------

const DocxView: React.FC<{ buffer: ArrayBuffer; url: string }> = ({ buffer, url }) => {
  const [content, setContent] = useState<DocxContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    parseDocxBuffer(buffer)
      .then((c) => {
        if (!cancelled) setContent(c);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to parse DOCX');
      });
    return () => {
      cancelled = true;
    };
  }, [buffer]);

  if (error) {
    return (
      <div className="native-doc-fallback">
        <p>Couldn't render this Word document.</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="pdf-link">
          Open in a new tab
        </a>
      </div>
    );
  }

  if (!content) {
    return <div className="native-doc-loading">Rendering document…</div>;
  }

  // Group consecutive list items into a single <ul> so styling is cohesive.
  const blocks: React.ReactNode[] = [];
  let listBuffer: DocxParagraphSlim[] = [];
  const flushList = (keyPrefix: number) => {
    if (listBuffer.length === 0) return;
    blocks.push(
      <ul key={`list-${keyPrefix}`} className="docx-list">
        {listBuffer.map((p, i) => (
          <li key={i}>{p.text}</li>
        ))}
      </ul>
    );
    listBuffer = [];
  };

  content.paragraphs.forEach((p, i) => {
    if (p.isList) {
      listBuffer.push({ text: p.text });
      return;
    }
    flushList(i);
    if (p.headingLevel) {
      const tag = `h${Math.min(6, Math.max(1, p.headingLevel))}`;
      blocks.push(React.createElement(tag, { key: i }, p.text || '\u00A0'));
    } else {
      blocks.push(
        <p key={i} className={p.text.trim() === '' ? 'docx-blank' : undefined}>
          {p.text || '\u00A0'}
        </p>
      );
    }
  });
  flushList(content.paragraphs.length);

  return (
    <div className="docx-viewport">
      <article className="docx-page">{blocks}</article>
    </div>
  );
};

interface DocxParagraphSlim {
  text: string;
}

// ---------------- PPTX (layout-faithful) ----------------

const luminance = (hex: string): number => {
  const m = hex.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return 1;
  const v = m[1];
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

const PptxView: React.FC<{ buffer: ArrayBuffer; url: string; title: string }> = ({
  buffer,
  url,
  title,
}) => {
  const [deck, setDeck] = useState<PptxDeck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // We stash the resolved deck in a ref-like closure so the cleanup can
    // revoke blob URLs even if the parse completed before we get here.
    let ownedUrls: string[] = [];
    parsePptxDeck(buffer)
      .then((d) => {
        if (cancelled) {
          // Component already unmounted — revoke so we don't leak.
          d.blobUrls.forEach((u) => URL.revokeObjectURL(u));
          return;
        }
        ownedUrls = d.blobUrls;
        setDeck(d);
        setActive(0);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to parse PPTX');
      });
    return () => {
      cancelled = true;
      ownedUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [buffer]);

  if (error) {
    return (
      <div className="native-doc-fallback">
        <p>Couldn't render this presentation.</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="pdf-link">
          Open in a new tab
        </a>
      </div>
    );
  }

  if (!deck) {
    return <div className="native-doc-loading">Rendering presentation…</div>;
  }

  if (deck.slides.length === 0) {
    return (
      <div className="native-doc-fallback">
        <p>This presentation has no slides.</p>
      </div>
    );
  }

  const currentIndex = Math.min(active, deck.slides.length - 1);

  return (
    <div className="pptx-viewport">
      <div className="pptx-stage-header">
        <span className="pptx-slide-index">
          Slide {currentIndex + 1} / {deck.slides.length}
        </span>
        <span className="pptx-slide-file" title={title}>
          {title}
        </span>
      </div>
      <SlideStage deck={deck} slideIndex={currentIndex} />
      <div className="pptx-filmstrip" role="tablist" aria-label="Slides">
        {deck.slides.map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`pptx-thumb ${i === active ? 'active' : ''}`}
            onClick={() => setActive(i)}
            aria-label={`Slide ${i + 1}`}
          >
            <div className="pptx-thumb-index">{i + 1}</div>
            <div className="pptx-thumb-canvas">
              <ScaledSlide deck={deck} slideIndex={i} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Wraps a natural-pixel slide in a container that watches its box size and
 * applies `transform: scale(fit)` so the deck fills the available space
 * without distorting. Used by both the main stage and the filmstrip thumbs.
 */
const ScaledSlide: React.FC<{ deck: PptxDeck; slideIndex: number; centered?: boolean }> = ({
  deck,
  slideIndex,
  centered,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const update = () => {
      const { width, height } = wrap.getBoundingClientRect();
      if (!width || !height) return;
      const s = Math.min(width / deck.width, height / deck.height);
      setScale(Number.isFinite(s) && s > 0 ? s : 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [deck.width, deck.height]);

  return (
    <div className={`pptx-scale-wrap ${centered ? 'centered' : ''}`} ref={wrapRef}>
      <div
        className="pptx-scale-box"
        style={{
          width: deck.width * scale,
          height: deck.height * scale,
        }}
      >
        <div
          className="pptx-scale-inner"
          style={{
            width: deck.width,
            height: deck.height,
            transform: `scale(${scale})`,
          }}
        >
          <SlideSurface deck={deck} slideIndex={slideIndex} />
        </div>
      </div>
    </div>
  );
};

const SlideStage: React.FC<{ deck: PptxDeck; slideIndex: number }> = ({ deck, slideIndex }) => (
  <div className="pptx-stage">
    <ScaledSlide deck={deck} slideIndex={slideIndex} centered />
  </div>
);

/** Renders a single slide at natural pixel dimensions (no scaling). */
const SlideSurface: React.FC<{ deck: PptxDeck; slideIndex: number }> = ({
  deck,
  slideIndex,
}) => {
  const slide = deck.slides[slideIndex];
  if (!slide) return null;

  const background = slide.background || '#ffffff';
  const defaultTextColor = luminance(background) < 0.5 ? '#ffffff' : '#111111';

  return (
    <div
      className="pptx-slide-surface"
      style={{
        width: deck.width,
        height: deck.height,
        background,
        color: defaultTextColor,
      }}
    >
      {slide.shapes.map((shape, i) => (
        <ShapeView key={i} shape={shape} />
      ))}
    </div>
  );
};

const ShapeView: React.FC<{ shape: PptxShape }> = ({ shape }) => {
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: shape.rect.x,
    top: shape.rect.y,
    width: shape.rect.width,
    height: shape.rect.height,
  };

  if (shape.kind === 'image') {
    return (
      <img
        src={shape.src}
        alt=""
        draggable={false}
        style={{ ...baseStyle, objectFit: 'cover' }}
        className="pptx-shape-image"
      />
    );
  }

  const justify =
    shape.verticalAlign === 'middle' ? 'center'
    : shape.verticalAlign === 'bottom' ? 'flex-end'
    : 'flex-start';

  return (
    <div
      className="pptx-shape-text"
      style={{
        ...baseStyle,
        background: shape.fill,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: justify,
        padding: `${shape.padding.top}px ${shape.padding.right}px ${shape.padding.bottom}px ${shape.padding.left}px`,
        boxSizing: 'border-box',
        overflow: 'hidden',
        color: shape.defaultTextColor,
        fontFamily: shape.defaultFontFamily,
      }}
    >
      {shape.paragraphs.map((p, i) => (
        <ParagraphView
          key={i}
          paragraph={p}
          defaultFontSizePt={shape.defaultFontSizePt}
          defaultTextColor={shape.defaultTextColor}
          defaultFontFamily={shape.defaultFontFamily}
        />
      ))}
    </div>
  );
};

const ParagraphView: React.FC<{
  paragraph: PptxParagraph;
  defaultFontSizePt: number;
  defaultTextColor?: string;
  defaultFontFamily?: string;
}> = ({ paragraph, defaultFontSizePt, defaultTextColor, defaultFontFamily }) => {
  const firstRun = paragraph.runs.find((r) => r.text !== '\n');
  const style: React.CSSProperties = {
    margin: 0,
    padding: 0,
    textAlign: paragraph.align,
    paddingLeft: paragraph.level * 18,
    // PPT lines are tight by default.
    lineHeight: 1.2,
    fontSize: `${firstRun?.fontSizePt || defaultFontSizePt}pt`,
    color: firstRun?.color || defaultTextColor,
    fontFamily: firstRun?.fontFamily || defaultFontFamily,
  };

  // Empty paragraphs still occupy a blank line in PPT — keep a nbsp so height
  // is preserved.
  const isBlank = paragraph.runs.every((r) => r.text.replace(/\n/g, '').trim() === '');

  const content = (
    <>
      {paragraph.bullet ? (
        <span className="pptx-bullet" aria-hidden>{paragraph.bulletChar || '•'} </span>
      ) : null}
      {isBlank ? '\u00A0' : paragraph.runs.map((r, i) => <RunView key={i} run={r} />)}
    </>
  );

  return (
    <p className="pptx-paragraph" style={style}>
      {content}
    </p>
  );
};

const RunView: React.FC<{ run: PptxTextRun }> = ({ run }) => {
  if (run.text === '\n') return <br />;
  const style: React.CSSProperties = {
    fontWeight: run.bold ? 700 : undefined,
    fontStyle: run.italic ? 'italic' : undefined,
    textDecoration: run.underline ? 'underline' : undefined,
    fontSize: run.fontSizePt ? `${run.fontSizePt}pt` : undefined,
    color: run.color,
    fontFamily: run.fontFamily,
    whiteSpace: 'pre-wrap',
  };
  return <span style={style}>{run.text}</span>;
};

// ---------------- XLSX ----------------

const XlsxView: React.FC<{ buffer: ArrayBuffer; url: string }> = ({ buffer, url }) => {
  const [sheets, setSheets] = useState<XlsxSheet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    parseXlsxBuffer(buffer)
      .then((s) => {
        if (!cancelled) {
          setSheets(s);
          setActive(0);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to parse XLSX');
      });
    return () => {
      cancelled = true;
    };
  }, [buffer]);

  if (error) {
    return (
      <div className="native-doc-fallback">
        <p>Couldn't render this spreadsheet.</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="pdf-link">
          Open in a new tab
        </a>
      </div>
    );
  }

  if (!sheets) {
    return <div className="native-doc-loading">Rendering spreadsheet…</div>;
  }

  const current = sheets[Math.min(active, sheets.length - 1)];
  const colCount = current.rows.reduce((max, r) => Math.max(max, r.length), 0);

  return (
    <div className="xlsx-viewport">
      <div className="xlsx-grid-wrap">
        <table className="xlsx-grid">
          <thead>
            <tr>
              <th className="xlsx-corner" />
              {Array.from({ length: colCount }, (_, i) => (
                <th key={i} className="xlsx-col-head">
                  {colLetter(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {current.rows.map((row, r) => (
              <tr key={r}>
                <th className="xlsx-row-head">{r + 1}</th>
                {Array.from({ length: colCount }, (_, c) => (
                  <td key={c} className="xlsx-cell" title={row[c] || ''}>
                    {row[c] || ''}
                  </td>
                ))}
              </tr>
            ))}
            {current.rows.length === 0 && (
              <tr>
                <td colSpan={colCount + 1} className="xlsx-empty">
                  Empty sheet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="xlsx-tabs" role="tablist" aria-label="Sheets">
        {sheets.map((s, i) => (
          <button
            key={s.name + i}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={`xlsx-tab ${i === active ? 'active' : ''}`}
            onClick={() => setActive(i)}
          >
            {s.name}
          </button>
        ))}
      </div>
    </div>
  );
};

/** 0-based column index -> Excel letter (0 -> "A", 26 -> "AA"). */
const colLetter = (index: number): string => {
  let n = index;
  let out = '';
  while (n >= 0) {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  }
  return out;
};

export default NativeDocViewer;
export { NATIVE_EXTENSIONS, getExtension };
